import { hubLangFor } from '../lib/i18n'
import { STARS } from '../lib/hubCopy'
import type { StarBucket, StarRange } from '../lib/stars'

/**
 * Stars you earned over time, as bars. REC-8 drew twelve months of stars
 * RECEIVED on the rail card (073); REC-57 (106, David 24 Sep) made it every
 * credit to the balance — recognitions AND activity, the welcome included —
 * and gave it four ranges: 30 days by day, 3 months by week, 12 months and all
 * time by month. The same component draws the rail card's 12 months and the
 * modal's four.
 *
 * ── IT IS A LIST, NOT AN SVG, AND THAT IS NOT LAZINESS ──────────────────────
 *
 * Every bar is a list item carrying its own readable sentence, keyboard focus
 * works without inventing anything, it renders under `renderToString` so the
 * suite can mount it, and CSS grid already knows how to reflow at 320px. An
 * SVG would need a viewBox, a text layer, a `role="img"` with a summary that
 * starts drifting from the data, and a second answer to "how tall is a bar".
 *
 * ── DATE LABELS ARE FORMATTED IN UTC, AND THE BUG IF THEY ARE NOT IS SILENT ─
 *
 * `start` is a date-only string, `2026-09-01`. `new Date('2026-09-01')` parses
 * as UTC MIDNIGHT, so `toLocaleDateString` in any negative-offset zone — every
 * Conetec office in the Americas — renders it as the day BEFORE: every month
 * bar labelled one month early, every day bar one day early, correctly ordered,
 * nothing on screen looking wrong. Hence `timeZone: 'UTC'` on every format. The
 * DATE is already the right one: the server bucketed days and weeks in the
 * person's own timezone and months in the UTC star month. Formatting only has
 * to not move it.
 *
 * ── HEIGHTS ARE RELATIVE TO THE BIGGEST BAR, AND THERE IS NO AXIS ───────────
 *
 * This is a shape, not a measurement — the figure that matters is printed
 * above it. The welcome's 1,000 makes month one a tall bar for everybody; that
 * is David's call ("everything, everywhere") and a fixed scale would only move
 * the problem to the other months.
 *
 * ── AN ALL-ZERO RANGE: NOTHING ON THE RAIL, A SENTENCE IN THE MODAL ─────────
 *
 * Flat bars are what a broken chart looks like. On the rail card the chart
 * simply is not drawn; in the modal, where somebody chose the range, silence
 * would read as a fault, so it says so in words.
 *
 * ── STILL NO RANKING (D10, upheld by B22) ───────────────────────────────────
 *
 * The reader's own history and nobody else's — `my_star_series()` takes no
 * person argument and has no admin variant. It compares you to your own past,
 * which is the one comparison this feature is allowed to draw.
 */
export type StarBar = {
  start: string
  stars: number
  /** 0-100, relative to the biggest bucket in the window. */
  pct: number
  /** The axis label, or '' where a label would crowd its neighbours. */
  tick: string
  /** "September 2026: 250 stars" — the readable sentence. */
  label: string
}

/**
 * The geometry, as a pure function so it can be tested without a DOM: the
 * things that can be wrong here are a date label off by one (see above), a bar
 * scaled against the wrong maximum, the singular of a one-star bucket, and a
 * thirty-bar row with thirty labels on it.
 */
export function chartBars(buckets: StarBucket[], locale = 'en-CA'): StarBar[] {
  const lang = hubLangFor(locale)
  const t = STARS[lang]
  const max = buckets.reduce((m, b) => Math.max(m, b.stars), 0)
  const n = buckets.length
  // Month ranges longer than a year ('all', eventually) label every k-th bar.
  const everyK = Math.max(1, Math.ceil(n / 12))

  return buckets.map((b, i) => {
    const d = dateOf(b.start)
    let period: string
    let tick = ''
    if (b.granularity === 'day') {
      period = d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
      // One label a week, counted back from the LAST bar so today is labelled.
      if ((n - 1 - i) % 7 === 0) tick = d.toLocaleDateString(locale, { day: 'numeric', timeZone: 'UTC' })
    } else if (b.granularity === 'week') {
      period = t.weekOf(d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }))
      // The month's name on the first week that starts in it.
      const prev = i > 0 ? dateOf(buckets[i - 1].start) : null
      if (!prev || prev.getUTCMonth() !== d.getUTCMonth()) {
        tick = d.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' })
      }
    } else {
      period = d.toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
      if ((n - 1 - i) % everyK === 0) tick = d.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' })
    }
    return {
      start: b.start,
      stars: b.stars,
      // A bucket with stars in it always draws SOMETHING: a bar rounding to 0%
      // beside the welcome month is a month that looks empty and is not.
      pct: max === 0 ? 0 : Math.max(b.stars > 0 ? 6 : 0, Math.round((b.stars / max) * 100)),
      tick,
      label: b.stars === 0 ? t.historyNothing(period) : t.historyAmount(period, b.stars),
    }
  })
}

/** `2026-09-01` → that instant at UTC midnight, without touching the parser. */
function dateOf(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1))
}

export function starHistorySum(buckets: StarBucket[]): number {
  return buckets.reduce((n, b) => n + b.stars, 0)
}

export function StarHistory({
  buckets,
  range = '12m',
  locale = 'en-CA',
  tall = false,
  sayWhenEmpty = false,
}: {
  buckets: StarBucket[]
  range?: StarRange
  locale?: string
  /** The modal's taller track. */
  tall?: boolean
  /** In the modal a chosen range with nothing in it says so; on the rail it draws nothing. */
  sayWhenEmpty?: boolean
}) {
  const t = STARS[hubLangFor(locale)]
  const sum = starHistorySum(buckets)
  if (buckets.length === 0 || sum === 0) {
    return sayWhenEmpty ? <p className="empty">{t.nothingInRange}</p> : null
  }

  const bars = chartBars(buckets, locale)
  const dense = bars.length > 16

  return (
    <div className={`spark${tall ? ' spark--tall' : ''}`}>
      <p className="spark__sum">
        ★ {sum.toLocaleString()} <span className="spark__sumlabel">{t.rangeTotal[range]}</span>
      </p>
      <ul
        className={`spark__bars${dense ? ' spark__bars--dense' : ''}`}
        // The column count is the bucket count, inline, because the CSS cannot
        // know it: 30 days, 13 weeks, 12 months or however many months "all" is.
        // A fixed count would draw a different period, silently.
        style={{ gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))` }}
      >
        {bars.map((b) => (
          <li className="spark__col" key={b.start}>
            {/* The bar is a div inside the li so the column keeps its full
                height and the bars share one baseline. `title` gives a mouse
                the sentence; the visually-hidden span gives it to everyone
                else, which is why both exist rather than just the title. */}
            <div className="spark__track" title={b.label}>
              <div
                className={`spark__bar${b.stars === 0 ? ' spark__bar--none' : ''}`}
                style={{ blockSize: `${b.pct}%` }}
              />
            </div>
            <span className="spark__tick" aria-hidden="true">
              {b.tick}
            </span>
            <span className="sr-only">{b.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
